import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { AnimatedTextField, Button, Column, FullRow, Row, Typography } from 'seagull';
import { ArrowUp } from 'seagull/icons';
import { usePromptSuggestions } from '~/hooks/reactQuery/queries/usePromptSuggestions';
import { useLocaleKeys } from '~/i18n';
import { useStoreActions } from '~/store/store-context';
import styles from './HelpMeWriteTextField.module.scss';
import { HelpMeWriteTextFieldTestIds, PresetType } from './HelpMeWriteTextField.types';
import PresetButton from './PresetButton';
const HelpMeWriteTextField = (param)=>{
    let { value, onChange, onSubmit, onPresetClick, isLoading = false, disabled = false, conversationId = '', isUndoLoading = false } = param;
    const { logBI } = useStoreActions();
    const { t: textFieldTexts } = useLocaleKeys('helpMeWrite.textField');
    const isDisabled = disabled || isLoading || isUndoLoading;
    const { suggestions } = usePromptSuggestions();
    const textFieldRef = useRef(null);
    const handlePresetClick = (preset)=>{
        const eventIdMap = {
            [PresetType.Formalize]: 17004,
            [PresetType.Polish]: 17005,
            [PresetType.Shorten]: 17006,
            [PresetType.Elaborate]: 17007
        };
        logBI(eventIdMap[preset]);
        onPresetClick(preset);
    };
    const handleSubmit = ()=>{
        if (value === null || value === void 0 ? void 0 : value.trim()) {
            logBI(17009, {
                text: value.trim()
            });
        }
        onChange('');
        onSubmit();
    };
    const handleKeyDown = (e)=>{
        if (e.key === 'Enter' && !e.shiftKey && !isDisabled) {
            e.preventDefault();
            handleSubmit();
        }
    };
    const presetButtons = [
        {
            type: PresetType.Formalize,
            onClick: ()=>handlePresetClick(PresetType.Formalize)
        },
        {
            type: PresetType.Polish,
            onClick: ()=>handlePresetClick(PresetType.Polish)
        },
        {
            type: PresetType.Elaborate,
            onClick: ()=>handlePresetClick(PresetType.Elaborate)
        },
        {
            type: PresetType.Shorten,
            onClick: ()=>handlePresetClick(PresetType.Shorten)
        }
    ];
    const animatedSuggestions = conversationId ? [] : suggestions;
    useEffect(()=>{
        if (!isDisabled && textFieldRef.current) {
            const timeoutId = setTimeout(()=>{
                var _textFieldRef_current;
                const textarea = (_textFieldRef_current = textFieldRef.current) === null || _textFieldRef_current === void 0 ? void 0 : _textFieldRef_current.querySelector('textarea');
                if (textarea) {
                    textarea.focus();
                }
            }, 300);
            return ()=>clearTimeout(timeoutId);
        }
    }, [
        isDisabled
    ]);
    return /*#__PURE__*/ _jsxs(Column, {
        testId: HelpMeWriteTextFieldTestIds.Root(),
        gap: 8,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.textFieldWrapper,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.textFieldContainer,
                        ref: textFieldRef,
                        children: /*#__PURE__*/ _jsx(AnimatedTextField, {
                            suggestions: animatedSuggestions,
                            collapseHelperText: false,
                            multiline: true,
                            minRows: 3,
                            maxRows: 5,
                            value: value,
                            onChange: (e)=>onChange(e.target.value),
                            inputProps: {
                                onKeyDown: handleKeyDown
                            },
                            disabled: isDisabled,
                            testId: HelpMeWriteTextFieldTestIds.TextField(),
                            inputRootClass: styles.inputRoot,
                            placeholder: textFieldTexts.placeholder()
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.footer,
                        children: /*#__PURE__*/ _jsxs(FullRow, {
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: 12,
                            children: [
                                /*#__PURE__*/ _jsx(Row, {
                                    gap: 8,
                                    children: presetButtons.map((param)=>{
                                        let { type, onClick } = param;
                                        return /*#__PURE__*/ _jsx(PresetButton, {
                                            onClick: onClick,
                                            disabled: isDisabled,
                                            testId: HelpMeWriteTextFieldTestIds[type](),
                                            text: type
                                        }, type);
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    bi: {
                                        name: 'run_prompt'
                                    },
                                    onClick: handleSubmit,
                                    disabled: isDisabled,
                                    testId: HelpMeWriteTextFieldTestIds.SubmitButton(),
                                    prefixIcon: /*#__PURE__*/ _jsx(ArrowUp, {
                                        size: 16
                                    }),
                                    className: styles.submitButton
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Typography, {
                className: styles.footerText,
                variant: "paragraph2",
                skin: "secondary",
                testId: HelpMeWriteTextFieldTestIds.Footer(),
                children: textFieldTexts.disclaimer()
            })
        ]
    });
};
export default HelpMeWriteTextField;
